'use client'

import { Button } from '@brew/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@brew/ui/dialog'
import { XIcon } from 'lucide-react'
import Image from 'next/image'
import { useCallback } from 'react'

export type AttachmentPreviewDialogProps = {
  open: boolean
  filename: string
  mediaType?: string
  url: string
  isImage: boolean
  onOpenChange: (open: boolean) => void
}

export function AttachmentPreviewDialog({
  open,
  filename,
  mediaType,
  url,
  isImage,
  onOpenChange,
}: AttachmentPreviewDialogProps) {
  const attachmentLabel = filename || (isImage ? 'Image' : 'Attachment')

  const handlePreventAutoFocus = useCallback((event: Event) => {
    event.preventDefault()
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-auto max-w-[min(32rem,calc(100vw-2rem))] gap-0 p-0"
        showCloseButton={false}
        onOpenAutoFocus={handlePreventAutoFocus}
      >
        <DialogTitle className="sr-only">{attachmentLabel} preview</DialogTitle>
        <div className="flex items-center justify-end p-3">
          <Button
            aria-label="Close preview"
            className="size-7 shrink-0"
            onClick={() => onOpenChange(false)}
            type="button"
            variant="ghost"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        {isImage ? (
          <div className="flex max-h-[min(32rem,calc(100vh-8rem))] w-full items-center justify-center overflow-hidden px-3">
            <Image
              alt={filename || 'attachment preview'}
              className="max-h-full max-w-full rounded-lg object-contain"
              src={url}
              unoptimized
              width={1024}
              height={1024}
              sizes="(max-width: 768px) 100vw, 32rem"
              quality={75}
            />
          </div>
        ) : null}
        <div className="p-3 pt-2">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h4 className="truncate font-semibold text-sm leading-none">
              {attachmentLabel}
            </h4>
            {mediaType ? (
              <p className="truncate font-mono text-muted-foreground text-xs">
                {mediaType}
              </p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
